import { useEffect, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import MainAppBar from 'components/MainAppBar';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { requestCustomerLoginLink } from 'utility/requests';
import { useCustomerAuth } from 'context/CustomerAuthContext';
import { email as isValidEmail } from 'utility/validation';

export default function SelfServeLogin() {
    const { i18n } = useLingui();
    const { status: authStatus } = useCustomerAuth();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle');
    const [hasSentEmail, setHasSentEmail] = useState(false);
    const [cooldownUntil, setCooldownUntil] = useState(0);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const submitting = useRef(false);

    useEffect(() => {
        if (!cooldownUntil) return;

        const timer = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
            setSecondsRemaining(remaining);
            if (!remaining) setCooldownUntil(0);
        }, 1000);

        return () => clearInterval(timer);
    }, [cooldownUntil]);

    async function handleSubmit(event) {
        event.preventDefault();
        if (
            submitting.current ||
            status === 'rate-limited' ||
            Date.now() < cooldownUntil ||
            !isValidEmail(email.trim())
        )
            return;

        if (hasSentEmail) {
            setHasSentEmail(false);
            setCooldownUntil(0);
            setSecondsRemaining(0);
            setStatus('idle');
            return;
        }

        submitting.current = true;
        setStatus('loading');
        try {
            const responseStatus = await requestCustomerLoginLink(email.trim(), i18n.locale);
            if (responseStatus !== 202) {
                setStatus('error');
                return;
            }
            setHasSentEmail(true);
            setCooldownUntil(Date.now() + 30000);
            setSecondsRemaining(30);
            setStatus('sent');
        } catch (error) {
            if (error.status === 400) {
                setHasSentEmail(false);
                setCooldownUntil(0);
                setSecondsRemaining(0);
                setStatus('invalid-email');
            } else if (error.status === 429) {
                setCooldownUntil(0);
                setSecondsRemaining(0);
                setStatus('rate-limited');
            } else {
                setStatus('error');
            }
        } finally {
            submitting.current = false;
        }
    }

    return (
        <>
            <MainAppBar />
            <div className="mx-auto w-full max-w-screen-sm px-4 pt-8">
                <Card className="border-solid border-border shadow-none">
                    <CardContent className="p-6 sm:p-8">
                        <h1 className="mt-0 text-2xl font-bold text-foreground">
                            <Trans>Find Your Tickets</Trans>
                        </h1>
                        {authStatus === 'error' && !hasSentEmail && (
                            <p role="alert" className="text-destructive mb-2">
                                <Trans>
                                    Your ticket access link could not be verified. It may be expired
                                    or invalid.
                                </Trans>
                            </p>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {hasSentEmail ? (
                                <>
                                    <p role="status">
                                        <strong className="text-lg block break-all mb-2">
                                            {email.trim()}
                                        </strong>
                                        <Trans>
                                            If this email address is valid, you'll receive a link to
                                            access your tickets.
                                        </Trans>
                                    </p>
                                    <p>
                                        <Trans>
                                            If you don't receive your email within a few minutes or
                                            you entered the wrong address, click Retry to edit your
                                            email and send another link.
                                        </Trans>
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p>
                                        <Trans>
                                            Enter the email address used for your order. We'll send
                                            you a link to access your tickets.
                                        </Trans>
                                    </p>
                                    <label htmlFor="ticket-lookup-email" className="block text-sm">
                                        <Trans>Email Address</Trans>
                                    </label>
                                    <Input
                                        id="ticket-lookup-email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        disabled={status === 'loading' || status === 'rate-limited'}
                                        onChange={(event) => {
                                            setEmail(event.target.value);
                                            if (status !== 'rate-limited') setStatus('idle');
                                        }}
                                    />
                                </>
                            )}
                            {status === 'invalid-email' && (
                                <p role="alert" className="text-sm text-destructive">
                                    <Trans>Please enter a valid email address.</Trans>
                                </p>
                            )}
                            {status === 'rate-limited' && (
                                <p role="alert" className="text-sm text-destructive">
                                    <Trans>Too many requests. Please try again later.</Trans>
                                </p>
                            )}
                            {status === 'error' && (
                                <p role="alert" className="text-sm text-destructive">
                                    <Trans>Unable to send your login link. Please try again.</Trans>
                                </p>
                            )}
                            <Button
                                type="submit"
                                disabled={
                                    status === 'loading' ||
                                    status === 'rate-limited' ||
                                    secondsRemaining > 0 ||
                                    !isValidEmail(email.trim())
                                }
                            >
                                {status === 'loading'
                                    ? t`Sending...`
                                    : secondsRemaining > 0
                                      ? t`Retry in ${secondsRemaining}s`
                                      : hasSentEmail
                                        ? t`Retry`
                                        : t`Send Ticket Link`}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
